import React from 'react'
import { Link } from 'react-router-dom'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'

import { FlowSessionState, SearchableField } from 'common/interfaces'

interface SearchResultsProps {
  results: FlowSessionState[]
  flowSlug: string | null
  searchFields: SearchableField[] | undefined
}

const SearchResults: React.FC<SearchResultsProps> = ({ results, flowSlug, searchFields }) => {
  // Helper function to render the full_data as a key-value list
  const renderKeyValueList = (data: Record<string, any>): React.ReactNode => (
    <List dense>
      {Object.entries(data).map(([key, value]) => (
        <ListItem key={key} sx={{ pl: 2 }}>
          <ListItemText
            primary={<strong>{key}</strong>}
            secondary={
              typeof value === 'object' && value !== null
                ? JSON.stringify(value, null, 2) // Handle nested objects
                : value
            }
          />
        </ListItem>
      ))}
    </List>
  )

  const renderSearchElements = (
    fields: SearchableField[] | undefined,
    values: Record<string, any>
  ): React.ReactNode[] => {
    const elements: React.ReactNode[] = []
    if (fields === undefined) return elements

    for (const field of fields) {
      const { name } = field.element

      // Check if the key exists in the values object
      if (name in values) {
        const value = values[name]

        elements.push(
          <Typography key={field.id}>
            <strong>{String(field.element.props.label) || String(field.element.name)}:</strong>{' '}
            {String(value)}
          </Typography>
        )
      } else {
        elements.push(
          <Typography key={field.id}>
            <strong>{String(field.element.props.label) || String(field.element.name)}:</strong>{' '}
            <i>None</i>
          </Typography>
        )
      }
    }

    return elements
  }

  return (
    <Box sx={{ mt: 3 }}>
      {results.map((session) => (
        <Accordion key={session.id} sx={{ mb: 2 }}>
          {/* Summary */}
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`session-content-${session.id}`}
            id={`session-header-${session.id}`}
          >
            <Box>
              {renderSearchElements(searchFields, session.full_data).map((element) => element)}
              <Typography>
                <strong>Created Date:</strong> {new Date(session.created).toLocaleString()}
              </Typography>
              <Typography>
                <strong>Modified Date:</strong> {new Date(session.modified).toLocaleString()}
              </Typography>
            </Box>
          </AccordionSummary>

          {/* Details */}
          <AccordionDetails>
            <Typography variant="body1" gutterBottom>
              <strong>Session Details:</strong>
            </Typography>
            {renderKeyValueList(session.full_data)}

            {/* Action Button */}
            <Button
              component={Link}
              to={flowSlug && flowSlug ? `/${flowSlug}?s_id=${session.id}` : ''}
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
            >
              Continue Session
            </Button>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Fallback for No Results */}
      {results.length === 0 && (
        <Typography variant="body1" color="text.secondary" align="center">
          No sessions found. Change your search criteria and try again. At least one field is
          required.
        </Typography>
      )}
    </Box>
  )
}

export default SearchResults
